import { FC } from "react";
import { useFormContext } from "react-hook-form";
import { IdNameList } from "../../../../../api/services/common/models";
import {
  FormController,
  MuiGrid,
  MuiSelect,
  MuiSelectWithTextInput,
  OptionsDataType,
} from "../../../../../components";
import {
  MAX_LENGTH_100,
  MAX_LENGTH_32,
} from "../../../../../helpers/constants";
import { setApiDataToDropDownOption } from "../../../../../helpers/utils";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { ProgramSearchProgramFormKey } from "../../../modelsAndSchemas/models";
import { programSearchSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { ParticipantsNameFields } from "../participantsNameFields/ParticipantsNameFields";

interface GenericParticipantSearchFieldsProps {
  pageId: string;
  filterDropdownValue: OptionsDataType[];
  solutionOfferingsData: IdNameList[];
  handleSolutionOfferingChange: (id: string) => void;
}
export const GenericParticipantSearchFields: FC<
  GenericParticipantSearchFieldsProps
> = ({
  pageId,
  filterDropdownValue,
  solutionOfferingsData,
  handleSolutionOfferingChange,
}) => {
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const { control } = useFormContext();
  const { commonLabels, useFetchLabel, fetchDropdownValues, commonValidationMessages } = useSiteCore();

  const getLabel = useFetchLabel(programSearchSiteCoreKeys.searchFormKey);

  const { commonARIALabel } = useNavigationSiteCore();

  return (
    <MuiGrid container className="form-container">
      <ParticipantsNameFields
        filterDropdownValue={filterDropdownValue}
        pageId={pageId}
      />
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.genericAccountName}
        >
          <MuiSelectWithTextInput
            options={filterDropdownValue}
            label={getLabel(programSearchSiteCoreKeys.genericAccountName)}
            placeholder={commonLabels.inputPlaceHolder}
            control={control}
            selectBoxName={ProgramSearchProgramFormKey.genericAccountNameFilter}
            id={`${pageId}-${ProgramSearchProgramFormKey.genericAccountName}`}
            selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.genericAccountNameFilter}`}
            maxLength={MAX_LENGTH_100}
            requiredARIALabel={commonARIALabel.required}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.genericRegistrationCode}
        >
          <MuiSelectWithTextInput
            options={filterDropdownValue}
            label={getLabel(programSearchSiteCoreKeys.genericRegistrationCode)}
            placeholder={commonLabels.inputPlaceHolder}
            control={control}
            selectBoxName={
              ProgramSearchProgramFormKey.genericRegistrationCodeFilter
            }
            id={`${pageId}-${ProgramSearchProgramFormKey.genericRegistrationCode}`}
            selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.genericRegistrationCodeFilter}`}
            maxLength={MAX_LENGTH_32}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.participantEmail}
        >
          <MuiSelectWithTextInput
            options={filterDropdownValue}
            label={getLabel(programSearchSiteCoreKeys.emailIdLabel)}
            placeholder={commonLabels.emailPlaceHolder}
            control={control}
            selectBoxName={ProgramSearchProgramFormKey.participantEmailFilter}
            id={`${pageId}-${ProgramSearchProgramFormKey.participantEmail}`}
            selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.participantEmailFilter}`}
            maxLength={MAX_LENGTH_100}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.clientName}
        >
          <MuiSelectWithTextInput
            options={filterDropdownValue}
            label={getLabel(programSearchSiteCoreKeys.clientLabel)}
            placeholder={commonLabels.inputPlaceHolder}
            control={control}
            selectBoxName={ProgramSearchProgramFormKey.clientNameFilter}
            id={`${pageId}-${ProgramSearchProgramFormKey.clientName}`}
            selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.clientNameFilter}`}
            maxLength={MAX_LENGTH_100}
            requiredARIALabel={commonARIALabel.required}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.nextView}
        >
          <MuiSelect
            id={`${pageId}-${ProgramSearchProgramFormKey.nextView}`}
            label={getLabel(programSearchSiteCoreKeys.nextViewLabel)}
            visibleDefaultSelectOption
            options={setApiDataToDropDownOption(solutionOfferingsData)}
            onChange={(e) =>
              handleSolutionOfferingChange(e.target.value as string)
            }
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.genericAccountStatus}
        >
          <MuiSelect
            id={`${pageId}-${ProgramSearchProgramFormKey.genericAccountStatus}`}
            label={getLabel(programSearchSiteCoreKeys.genericAccountStatus)}
            options={fetchDropdownValues(
              programSearchSiteCoreKeys.searchFormKey,
              programSearchSiteCoreKeys.genericAccountStatusList,
            )}
            visibleDefaultSelectOption={false}
            requiredARIALabel={commonARIALabel.required}
            placeholder={commonLabels.selectPlaceHolder}
          />
        </FormController>
      </MuiGrid>
    </MuiGrid>
  );
};
